import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { InternalSearchCriteria } from '../../interfaces/internal-search-criteria';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Compact order-search "pill" used in the application top menubars.
 *
 * Renders a "Search by" type selector, a search input, and a submit button,
 * and owns the navigation to the orders list / order detail. Both the client
 * and admin portals share this; admin opts into the extra "External Order
 * Number" option via showExternalOrderNumber.
 */
@Component({
    selector: 'lib-search-pill',
    templateUrl: './search-pill.component.html',
    styleUrls: ['./search-pill.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchPillComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  /** When true, adds an "External Order Number" search option (admin only). */
  @Input() showExternalOrderNumber = false;

  /** Emitted on submit, for consumers that want to react to a search. */
  @Output() search = new EventEmitter<InternalSearchCriteria>();

  protected searchOptions = [
    { value: 'businessName', label: 'Business Name' },
    { value: 'dateOfBirth', label: 'Date of Birth' },
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'orderId', label: 'OrderID' },
    { value: 'referenceNumber', label: 'Reference Number' },
    { value: 'registrationOrFileNumber', label: 'Registration Number' },
    { value: 'serialNumber', label: 'Serial Number' }
  ];

  protected searchCriteria: string = '';
  protected typeOfSearch: string = 'referenceNumber';

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  constructor(private router: Router) { }

  ngOnInit(): void {
    if (this.showExternalOrderNumber) {
      const externalOrderNumberOption = { value: 'externalOrderNumber', label: 'External Order Number' };
      const index = this.searchOptions.findIndex(option => option.value === 'dateOfBirth');

      if (index !== -1) {
        this.searchOptions.splice(index + 1, 0, externalOrderNumberOption);
      }
    }
  }

  protected get selectedSearchLabel(): string {
    const option = this.searchOptions.find(option => option.value === this.typeOfSearch);
    return option ? this.transloco.translate('searchOptions.' + option.value) : '';
  }

  // Switch the search type and drop focus straight into the input so the
  // user can start typing without an extra click.
  protected selectSearchType(value: string) {
    this.typeOfSearch = value;
    this.focusSearchInput();
  }

  private focusSearchInput() {
    // Defer until the input is rendered/visible (e.g. after the menu closes).
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  protected performSearch() {
    const criteria = this.searchCriteria.trim();
    if (!criteria) {
      return;
    }

    const search: InternalSearchCriteria = { typeOfSearch: this.typeOfSearch, criteria };
    this.search.emit(search);
    this.onSearch(search);
    this.searchCriteria = '';
  }

  private onSearch(search: InternalSearchCriteria) {
    switch (search.typeOfSearch) {
      case 'referenceNumber':
        this.searchByReferenceNumber(search.criteria);
        break;
      case 'firstName':
        this.searchByFirstName(search.criteria);
        break;
      case 'lastName':
        this.searchByLastName(search.criteria);
        break;
      case 'businessName':
        this.searchByBusinessName(search.criteria);
        break;
      case 'serialNumber':
        this.searchBySerialNumber(search.criteria);
        break;
      case 'dateOfBirth':
        this.searchByDateOfBirth(search.criteria);
        break;
      case 'orderId':
        this.searchByOrderId(search.criteria);
        break;
      case 'registrationOrFileNumber':
        this.searchByRegistrationOrFileNumber(search.criteria);
        break;
      case 'externalOrderNumber':
        this.searchByExternalOrderNumber(search.criteria);
        break;
      default:
        console.error('Invalid search option');
        break;
    }
  }

  private searchByReferenceNumber(referenceNumber: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, referenceNumber: referenceNumber }
    });
  }

  private searchByFirstName(firstName: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, firstName: firstName }
    });
  }

  private searchByLastName(lastName: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, lastName: lastName }
    });
  }

  private searchByBusinessName(businessName: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, businessName: businessName }
    });
  }

  private searchBySerialNumber(serialNumber: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, serialNumber: serialNumber }
    });
  }

  private searchByDateOfBirth(dateOfBirth: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, dateOfBirth: dateOfBirth }
    });
  }

  private searchByRegistrationOrFileNumber(registrationNumber: string) {
    const lienOrderTypes = [
      'Lien',
      'Renewal',
      'Amendment',
      'Discharge',
      'BasicRenewal',
      'BasicDischarge'
    ];

    this.router.navigate(['/orders'], {
      queryParams: {
        autoExpand: false,
        autoRedirect: true,
        orderTypes: lienOrderTypes,
        registrationOrFileNumber: registrationNumber
      }
    });
  }

  private searchByExternalOrderNumber(externalOrderNumber: string) {
    this.router.navigate(['/orders'], {
      queryParams: { autoExpand: false, autoRedirect: true, externalOrderNumber: externalOrderNumber }
    });
  }

  private searchByOrderId(orderId: string) {
    this.router.navigate(['orders', orderId]);
  }
}
